import React, { useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import QRCodeDisplay, { QRCodeDisplayRef } from '../components/QRCodeDisplay';

const ClientDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const qrCodeRef = useRef<QRCodeDisplayRef>(null);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('clientDashboard')}</h1>
              <p className="text-gray-600">{t('welcome')}, {user?.name}</p>
            </div>
            <button
              onClick={logout}
              className="btn-secondary"
            >
              {t('signOut')}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* QR Code Section */}
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('yourQRCode')}
              </h2>
              <QRCodeDisplay ref={qrCodeRef} />
            </div>

            {/* Check-in History */}
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('recentCheckIns')}
              </h2>
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">{t('noRecentCheckIns')}</p>
                </div>
              </div>
            </div>

            {/* Client Info */}
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('yourInformation')}
              </h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('name')}</label>
                  <p className="text-gray-900">{user?.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('email')}</label>
                  <p className="text-gray-900">{user?.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('status')}</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    user?.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {user?.status === 'approved' ? t('approved') : t('pending')}
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('quickActions')}
              </h2>
              <div className="space-y-3">
                <button 
                  className="btn-primary w-full"
                  onClick={() => qrCodeRef.current?.refresh()}
                >
                  {t('generateNewQRCode')}
                </button>
                <button className="btn-secondary w-full">
                  {t('viewCheckInHistory')}
                </button>
                <button className="btn-secondary w-full">
                  {t('updateProfile')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClientDashboard; 